import React, { useEffect, useState } from "react";
import { YearAndPeriod } from "@/models/";
import { Apartment, Convert, ExpenseCategory } from "@/models/responses";
import { useAppData } from "@/hooks/useAppData";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../shadcn/ui/table";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { toast } from "@/lib/shadcn/hooks/use-toast";

interface ExpenseTableProps {
    apartments: Apartment[];
    expenseCategories: Map<number, ExpenseCategory>;
    yearAndPeriod: YearAndPeriod;
}

const ExpenseTable: React.FC<ExpenseTableProps> = ({ apartments, expenseCategories, yearAndPeriod }) => {
    const [totalExpensesPerApartment, setTotalExpensesPerApartment] = useState<Map<number, number>>(new Map());
    const [expensesByCategoryByApartment, setExpensesByCategoryByApartment] = useState<Map<number, Map<number, number>>>(
        new Map<number, Map<number, number>>(),
    );
    const [totalExpensesPerCategory, setTotalExpensesPerCategory] = useState<Map<number, number>>(new Map());
    const { selectedOwner } = useAppData();

    useEffect(() => {
        if (apartments.length === 0) {
            return;
        }
        let url = `${apiUrl}${getApiBasePath()}/owners/${selectedOwner}/expenses?aggregate=sum&year=${yearAndPeriod.year}&period=${yearAndPeriod.period}`;
        fetch(url, fetchOptions)
            .then((response) => {
                if (response) {
                    return response.text();
                }
                throw new Error("Unauthorized");
            })
            .then((data) => {
                const groupedExpenses = Convert.toGetGroupedByExpensesResponse(data).expenses;
                const totalExpensesPerApartment = new Map<number, number>();
                const totalExpensesPerCategory = new Map<number, number>();
                const expensesByCategoryByApartment = new Map<number, Map<number, number>>();
                groupedExpenses.forEach((expense) => {
                    totalExpensesPerCategory.set(
                        expense.categoryId,
                        (totalExpensesPerCategory.get(expense.categoryId) || 0) + expense.amount,
                    );
                    totalExpensesPerApartment.set(
                        expense.apartmentId,
                        (totalExpensesPerApartment.get(expense.apartmentId) || 0) + expense.amount,
                    );
                    if (!expensesByCategoryByApartment.has(expense.categoryId)) {
                        expensesByCategoryByApartment.set(expense.categoryId, new Map());
                    }
                    let categoryMap = expensesByCategoryByApartment.get(expense.categoryId);
                    if (categoryMap) {
                        categoryMap.set(expense.apartmentId, expense.amount);
                    }
                });
                setTotalExpensesPerApartment(totalExpensesPerApartment);
                setExpensesByCategoryByApartment(expensesByCategoryByApartment);
                setTotalExpensesPerCategory(totalExpensesPerCategory);
            })
            .catch((error) =>
                toast({
                    title: "Error",
                    description: `Failed to fetch expenses: ${error.message}`,
                    variant: "destructive",
                }),
            );
    }, [apartments, selectedOwner, yearAndPeriod.period, yearAndPeriod.year]);

    if (expensesByCategoryByApartment.size === 0) {
        return <p className="text-sm text-muted-foreground">No expenses recorded for this period.</p>;
    }

    return (
        <div className="w-full">
            <Table className="text-[11px]">
                <TableHeader className="bg-muted">
                    <TableRow className="hover:bg-muted">
                        <TableHead className="py-2 px-1 text-left font-medium">Expense Type</TableHead>
                        {apartments.map((apartment) => (
                            <TableHead key={apartment.id} className="py-2 px-1 text-right font-medium">
                                {apartment.shortName || apartment.name}
                            </TableHead>
                        ))}
                        <TableHead className="py-2 px-1 text-right font-medium bg-muted">Total</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {Array.from(expensesByCategoryByApartment).map(([categoryId, apartmentExpensesForCategory]) => (
                        <TableRow key={categoryId} className="hover:bg-muted">
                            <TableCell className="py-2 px-1">{expenseCategories.get(categoryId)?.category}</TableCell>
                            {apartments.map((apartment) => (
                                <TableCell key={apartment.id} className="py-2 px-1 text-right whitespace-nowrap">
                                    {apartmentExpensesForCategory.get(apartment.id)
                                        ? `${apartmentExpensesForCategory.get(apartment.id)?.toFixed(2)} €`
                                        : "0.00 €"}
                                </TableCell>
                            ))}
                            <TableCell className="py-2 px-1 text-right font-bold bg-muted whitespace-nowrap">
                                {totalExpensesPerCategory.get(categoryId)
                                    ? `${totalExpensesPerCategory.get(categoryId)?.toFixed(2)} €`
                                    : "0.00 €"}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow className="hover:bg-muted font-bold">
                        <TableCell className="py-2 px-1">Total</TableCell>
                        {apartments.map((apartment) => (
                            <TableCell key={apartment.id} className="py-2 px-1 text-right whitespace-nowrap">
                                {totalExpensesPerApartment.get(apartment.id)
                                    ? `${totalExpensesPerApartment.get(apartment.id)?.toFixed(2)} €`
                                    : "0.00 €"}
                            </TableCell>
                        ))}
                        <TableCell className="py-2 px-1 text-right font-bold bg-muted whitespace-nowrap">
                            {`${Array.from(totalExpensesPerApartment.values())
                                .reduce((total, amount) => total + amount, 0)
                                .toFixed(2)} €`}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
};

export default ExpenseTable;
